import React from "react";
import { Download, FileText } from "lucide-react";
import { cn } from "@/lib/utils";

interface DocumentMessageProps {
  url: string;
  title: string;
  /** File size in bytes; rendered as a human-readable suffix when present. */
  size?: number;
  /** Lowercase extension (e.g. "pdf", "docx") for the icon corner badge. */
  extension?: string;
}

function formatSize(bytes?: number): string | null {
  if (!bytes || bytes <= 0) return null;
  const units = ["B", "KB", "MB", "GB"];
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value.toFixed(value >= 10 || unit === 0 ? 0 : 1)} ${units[unit]}`;
}

export const DocumentMessage: React.FC<DocumentMessageProps> = ({
  url,
  title,
  size,
  extension,
}) => {
  const sizeLabel = formatSize(size);
  const ext = (extension ?? "").toLowerCase();

  return (
    <div className="mt-2">
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        download={title}
        className={cn(
          "group flex items-center gap-3 rounded-xl border border-border",
          "bg-card/30 p-3 shadow-sm backdrop-blur-sm",
          "transition-colors hover:bg-card/60 hover:border-primary/30"
        )}
      >
        <div className="relative flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
          <FileText className="h-5 w-5" />
          {ext && (
            <span className="absolute -bottom-1 -right-1 rounded-sm bg-primary px-1 text-[9px] font-bold uppercase leading-tight text-primary-foreground">
              {ext}
            </span>
          )}
        </div>

        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-foreground">{title}</p>
          {sizeLabel && (
            <p className="text-xs text-muted-foreground">{sizeLabel}</p>
          )}
        </div>

        <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-muted-foreground transition-colors group-hover:bg-primary group-hover:text-primary-foreground">
          <Download className="h-4 w-4" />
        </div>
      </a>
    </div>
  );
};
